import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { appTooltipProps } from '../../utils';

// Extract clientX from mouse or touch event
const getClientX = (event) => {
  if (event.touches?.length) return event.touches[0].clientX;
  if (event.changedTouches?.length) return event.changedTouches[0].clientX;
  return event.clientX;
};

// Find the closest dot index given an array of center-X positions
const findClosestIdx = (clientX, positions) => {
  let closest = 0;
  let minDist = Infinity;
  positions.forEach((x, i) => {
    const dist = Math.abs(clientX - x);
    if (dist < minDist) {
      minDist = dist;
      closest = i;
    }
  });
  return closest;
};

//
// Stepped slider with labeled dot positions along a track.
//
// Layout: flex row of interleaved 0-width dot divs and flex-2 line divs,
// with flex-1 fillers at each end. Every line div contains an invisible
// copy of all labels (h-0 overflow-hidden) so they all share the same
// min-width (the widest label), keeping dots evenly spaced.
//
// Supports click, mouse drag, and touch drag (including Safari).
//
// levels: [{ code, labelKey }, ...] where index 0 is "none"
// levelIndex: current selected index (0 = none)
// onLevelChange: (newIndex) => void
// translate: translation function
// labelNamespace: translation namespace for level labels
// hintNamespace: optional translation namespace for hover hints
// disabled: boolean
//
function SelectSlider({
  levels,
  levelIndex,
  onLevelChange,
  translate,
  labelNamespace,
  hintNamespace,
  disabled,
  className,
}) {
  const dotEls = useRef([]);
  const dragState = useRef({ active: false, positions: [], lastIdx: -1 });
  const dragMoved = useRef(false);
  const onChangeRef = useRef(onLevelChange);
  onChangeRef.current = onLevelChange;

  // ── Window-level drag handlers (stable — only use refs) ──

  const onDragMove = useCallback(
    (event) => {
      if (!dragState.current.active) return;
      event.preventDefault();
      const idx = findClosestIdx(
        getClientX(event),
        dragState.current.positions,
      );
      if (idx !== dragState.current.lastIdx) {
        dragMoved.current = true;
        dragState.current.lastIdx = idx;
        onChangeRef.current(idx);
      }
    },
    [],
  );

  const onDragEnd = useCallback(
    () => {
      if (!dragState.current.active) return;
      dragState.current.active = false;
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
      window.removeEventListener('touchcancel', onDragEnd);
    },
    [onDragMove],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
      window.removeEventListener('touchcancel', onDragEnd);
    },
    [onDragMove, onDragEnd],
  );

  // ── Drag start — attached to the track container ──

  const handleDragStart = useCallback(
    (event) => {
      if (disabled) return;

      // Snapshot dot center-X positions (0-width divs: left IS center)
      const positions = dotEls.current.map((el) => {
        if (!el) return 0;
        return el.getBoundingClientRect().left;
      });

      const idx = findClosestIdx(getClientX(event), positions);
      dragState.current = { active: true, positions, lastIdx: idx };
      dragMoved.current = false;
      onChangeRef.current(idx);

      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragEnd);
      window.addEventListener('touchmove', onDragMove, { passive: false });
      window.addEventListener('touchend', onDragEnd);
      window.addEventListener('touchcancel', onDragEnd);
    },
    [disabled, onDragMove, onDragEnd],
  );

  // ── Click handler for keyboard (Enter/Space) accessibility ──

  const handleStepClick = useCallback(
    (event) => {
      if (disabled) return;
      // Skip click if it follows a drag that moved
      if (dragMoved.current) {
        dragMoved.current = false;
        return;
      }
      const idx = Number(event.currentTarget.dataset.index);
      onLevelChange(idx);
    },
    [disabled, onLevelChange],
  );

  // ── Render ──

  const lastIdx = levels.length - 1;

  // Invisible sizing block placed inside every line-div so they all
  // resolve to the same intrinsic min-width (the widest label text).
  const sizingBlock = (
    <div className="h-0 overflow-hidden">
      {levels.map((l) => (
        <div key={l.labelKey} className="text-[11px] whitespace-nowrap">
          {`${translate(l.labelKey, labelNamespace)}...`}
        </div>
      ))}
    </div>
  );

  // Build interleaved children:
  //  filler · dot · line · dot · line · … · dot · filler
  const children = [];

  levels.forEach((level, idx) => {
    const isSelected = (idx === levelIndex);
    const isInActiveZone = (idx <= levelIndex);

    // ── Dot: 0-width container, content overflows and is centred ──
    const hintText = hintNamespace ? translate(level.labelKey, hintNamespace) : null;

    children.push(
      <div
        key={`dot-${level.labelKey}`}
        ref={(el) => { dotEls.current[idx] = el; }}
        className="w-0 shrink-0 relative z-10"
        style={{ overflow: 'visible' }}
      >
        <button
          type="button"
          data-index={idx}
          onClick={handleStepClick}
          disabled={disabled}
          {...appTooltipProps(hintText)}
          data-tooltip-delay-show={hintText ? 400 : undefined}
          className={classNames(
            'flex flex-col items-center bg-transparent border-0 p-0 -translate-x-1/2',
            { 'cursor-pointer': !disabled, 'cursor-default': disabled },
          )}
        >
          {/* Circle */}
          <div
            className={classNames(
              'w-4 h-4 rounded-full border-2 p-0.5',
              'flex items-center justify-center bg-white',
              {
                'border-pxCheckActive': !disabled,
                'border-black': disabled && isInActiveZone,
                'border-black/20': disabled && (!isInActiveZone),
              },
            )}
          >
            {(isSelected || isInActiveZone) && (
              <div
                className={classNames(
                  'w-full h-full rounded-full',
                  {
                    'bg-pxCheckActive': (!disabled) && (isSelected),
                    'bg-pxCheckActive/30': (!disabled) && (!isSelected),
                    'bg-black/60': disabled && isSelected,
                    'bg-black/30': disabled && (!isSelected),
                  },
                )}
              />
            )}
          </div>

          {/* Label – always rendered for layout; invisible when hidden */}
          <span
            className={classNames(
              'text-[11px] mt-1 whitespace-nowrap',
              {
                'text-pxCheckActive font-semibold': isSelected && (!disabled) && (idx > 0),
                'text-black/60 font-semibold': isSelected && (disabled || (idx === 0)),
                'text-black/40': (!isSelected) && (!disabled),
                'invisible': (!isSelected) && disabled,
              },
            )}
          >
            {translate(level.labelKey, labelNamespace)}
          </span>
        </button>
      </div>,
    );

    // ── Line segment to the next dot ──
    if (idx < lastIdx) {
      const isLineActive = (idx < levelIndex);

      children.push(
        <div key={`line-${level.labelKey}`} className="shrink-0 relative">
          {/* Visible track segment */}
          <div
            className={classNames(
              'absolute left-0 right-0',
              isLineActive
                ? 'top-[6px] h-0.5 rounded'
                : 'top-[7px] h-px',
              {
                'bg-pxCheckActive': isLineActive && (!disabled),
                'bg-black': isLineActive && disabled,
                'bg-black/20': !isLineActive,
              },
            )}
          />
          {/* Invisible text – forces equal min-width across all line divs */}
          {sizingBlock}
        </div>,
      );
    }
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classNames(
        'w-fit flex items-start select-none px-10',
        className,
      )}
      style={{ touchAction: 'none' }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      {children}
    </div>
  );
}

SelectSlider.propTypes = {
  levels: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    labelKey: PropTypes.string.isRequired,
  })).isRequired,
  levelIndex: PropTypes.number.isRequired,
  onLevelChange: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  labelNamespace: PropTypes.string.isRequired,
  hintNamespace: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default SelectSlider;
